import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Clock, Users, Upload, CheckCircle, 
  Phone, Mail, User, Home, QrCode, AlertCircle, ChevronRight, Printer
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ScriptureHeader from '../components/ScriptureHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PantryRegistrationPage() {
  const [step, setStep] = useState(1);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(null);

  // Print QR Code function
  const handlePrintQR = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Food Pantry QR Code - ${registrationComplete?.registration_id}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            padding: 40px; 
            text-align: center;
            max-width: 400px;
            margin: 0 auto;
          }
          .header { 
            border-bottom: 2px solid #228B22; 
            padding-bottom: 20px; 
            margin-bottom: 20px;
          }
          .header h1 { 
            color: #228B22; 
            margin: 0;
            font-size: 24px;
          }
          .header p { 
            color: #666; 
            margin: 5px 0 0 0;
            font-size: 14px;
          }
          .qr-code { 
            margin: 30px 0;
          }
          .qr-code img { 
            width: 250px; 
            height: 250px;
            border: 2px solid #ddd;
            padding: 10px;
          }
          .details { 
            text-align: left;
            background: #f5f5f5;
            padding: 20px;
            border-radius: 10px;
            margin: 20px 0;
          }
          .details p { 
            margin: 8px 0;
            font-size: 14px;
          }
          .details strong { 
            display: inline-block;
            width: 120px;
          }
          .reg-id {
            font-family: monospace;
            font-size: 18px;
            font-weight: bold;
            background: #228B22;
            color: white;
            padding: 10px 20px;
            border-radius: 5px;
            display: inline-block;
            margin: 10px 0;
          }
          .footer { 
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 12px;
            color: #666;
          }
          @media print {
            body { padding: 20px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Living Fruit Ministry</h1>
          <p>Food Pantry Appointment</p>
        </div>
        
        <div class="reg-id">${registrationComplete?.registration_id}</div>
        
        <div class="qr-code">
          <p><strong>Show this QR code when you arrive:</strong></p>
          <img src="data:image/png;base64,${registrationComplete?.qr_code}" alt="QR Code" />
        </div>
        
        <div class="details">
          <p><strong>Date:</strong> ${registrationComplete?.appointment_date}</p>
          <p><strong>Time:</strong> ${registrationComplete?.appointment_time}</p>
          <p><strong>Household:</strong> ${formData.household_size} people</p>
        </div>
        
        <div class="footer">
          <p><strong>What to bring:</strong> Valid photo ID, bags for food items</p>
          <p>Living Fruit Ministry | (407) 216-2853 | livingfruitministry@gmail.com</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    household_size: 1,
    slot_id: '',
    id_photo_url: ''
  });

  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${API}/pantry/slots`);
      setSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Failed to load available appointments');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/pantry/upload-id-photo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData({ ...formData, id_photo_url: response.data.id });
      toast.success('Photo uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setFormData({ ...formData, slot_id: slot.id });
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.full_name.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!formData.slot_id) {
      toast.error('Please select an appointment time');
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/pantry/register`, formData);
      setRegistrationComplete(response.data);
      toast.success('Registration successful!');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Group slots by date
  const slotsByDate = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-cream">
        <ScriptureHeader 
          title="Registration Complete" 
          page="pantry"
        />
        
        <div className="max-w-2xl mx-auto px-4 py-12">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl p-8 text-center"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-600" size={40} />
            </div>
            
            <h2 className="text-2xl font-serif text-charcoal mb-2">
              Thank You for Registering!
            </h2>
            <p className="text-charcoal/60 mb-6">
              Your food pantry appointment has been confirmed.
            </p>

            <div className="bg-cream rounded-xl p-6 mb-6 text-left">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-charcoal/60">Registration ID</p>
                  <p className="font-mono font-bold text-charcoal">{registrationComplete.registration_id}</p>
                </div>
                <div>
                  <p className="text-charcoal/60">Date</p>
                  <p className="font-medium text-charcoal">{registrationComplete.appointment_date}</p>
                </div>
                <div>
                  <p className="text-charcoal/60">Time</p>
                  <p className="font-medium text-charcoal">{registrationComplete.appointment_time}</p>
                </div>
                <div>
                  <p className="text-charcoal/60">Household Size</p>
                  <p className="font-medium text-charcoal">{formData.household_size} people</p>
                </div>
              </div>
            </div>

            {/* QR Code */}
            <div className="mb-6">
              <p className="text-sm text-charcoal/60 mb-3">
                Show this QR code when you arrive:
              </p>
              <div className="inline-block p-4 bg-white border-2 border-charcoal/20 rounded-xl">
                <img 
                  src={`data:image/png;base64,${registrationComplete.qr_code}`}
                  alt="QR Code"
                  className="w-48 h-48 mx-auto"
                />
              </div>
              <p className="text-xs text-charcoal/50 mt-3">
                Staff will scan this QR code to check you in automatically.
                <br />Each QR code can only be used once per event date.
              </p>
            </div>

            {formData.email && (
              <p className="text-sm text-charcoal/60 mb-6">
                A confirmation email with your QR code has been sent to <strong>{formData.email}</strong>
              </p>
            )}

            <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 text-left">
              <p className="font-medium text-charcoal flex items-center gap-2 mb-2">
                <AlertCircle size={18} className="text-gold" />
                What to Bring
              </p>
              <ul className="text-sm text-charcoal/70 space-y-1">
                <li>• Valid photo ID</li>
                <li>• This QR code (printed or on your phone)</li>
                <li>• Bags or containers for food items</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <button
                onClick={handlePrintQR}
                className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-burgundy text-burgundy rounded-xl hover:bg-burgundy/5 transition-colors"
                data-testid="print-qr-btn"
              >
                <Printer size={18} />
                Print QR Code
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-6 py-3 bg-burgundy text-white rounded-xl hover:bg-burgundy/90 transition-colors"
              >
                Return to Homepage
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <ScriptureHeader 
        title="Food Pantry Registration" 
        page="pantry"
      />
      
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-12">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
                  step >= s 
                    ? 'bg-burgundy text-white' 
                    : 'bg-charcoal/10 text-charcoal/40'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 rounded-full transition-colors ${
                  step > s ? 'bg-burgundy' : 'bg-charcoal/10'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Personal Information */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <h2 className="text-xl font-serif text-charcoal mb-6 flex items-center gap-2">
                <User className="text-burgundy" />
                Personal Information
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-charcoal/20 rounded-xl focus:ring-2 focus:ring-burgundy focus:border-transparent"
                    data-testid="input-full-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(407) 555-1234"
                      className="w-full pl-12 pr-4 py-3 border border-charcoal/20 rounded-xl focus:ring-2 focus:ring-burgundy focus:border-transparent"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-2">
                    Email Address (optional - for QR code delivery)
                  </label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="w-full pl-12 pr-4 py-3 border border-charcoal/20 rounded-xl focus:ring-2 focus:ring-burgundy focus:border-transparent"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-2">
                    Household Size *
                  </label>
                  <div className="relative">
                    <Home size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" />
                    <select
                      value={formData.household_size}
                      onChange={(e) => setFormData({ ...formData, household_size: parseInt(e.target.value) })}
                      className="w-full pl-12 pr-4 py-3 border border-charcoal/20 rounded-xl focus:ring-2 focus:ring-burgundy focus:border-transparent appearance-none"
                      data-testid="input-household"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                        <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* ID Photo Upload */}
                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-2">
                    ID Photo (optional - for faster check-in)
                  </label>
                  <div className="border-2 border-dashed border-charcoal/20 rounded-xl p-6 text-center hover:border-burgundy/50 transition-colors">
                    {formData.id_photo_url ? (
                      <div className="flex items-center justify-center gap-3">
                        <CheckCircle className="text-green-600" size={24} />
                        <span className="text-green-700 font-medium">Photo uploaded</span>
                        <button
                          onClick={() => setFormData({ ...formData, id_photo_url: '' })}
                          className="text-red-500 text-sm hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          data-testid="input-photo"
                        />
                        {uploadingPhoto ? (
                          <div className="flex items-center justify-center gap-3">
                            <div className="w-5 h-5 border-2 border-burgundy border-t-transparent rounded-full animate-spin" />
                            <span className="text-charcoal/60">Uploading...</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="text-charcoal/40" size={32} />
                            <span className="text-charcoal/60">Click to upload ID photo</span>
                            <span className="text-xs text-charcoal/40">Max 5MB - JPG, PNG, or WebP</span>
                          </div>
                        )}
                      </label>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => {
                    if (!formData.full_name.trim()) {
                      toast.error('Please enter your full name');
                      return;
                    }
                    if (!formData.phone.trim()) {
                      toast.error('Please enter your phone number');
                      return;
                    }
                    setStep(2);
                  }}
                  className="w-full py-3 bg-burgundy text-white rounded-xl hover:bg-burgundy/90 transition-colors flex items-center justify-center gap-2"
                  data-testid="next-step-1"
                >
                  Continue
                  <ChevronRight size={18} />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Select Appointment */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <h2 className="text-xl font-serif text-charcoal mb-6 flex items-center gap-2">
                <Calendar className="text-burgundy" />
                Select Appointment Time
              </h2>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-burgundy border-t-transparent rounded-full animate-spin" />
                </div>
              ) : Object.keys(slotsByDate).length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="text-charcoal/30 mx-auto mb-4" size={48} />
                  <p className="text-charcoal/60">No appointment slots available at this time.</p>
                  <p className="text-sm text-charcoal/40 mt-2">Please check back later or call us for assistance.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(slotsByDate).map(([date, dateSlots]) => (
                    <div key={date}>
                      <p className="font-medium text-charcoal mb-3">
                        {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {dateSlots.map((slot) => (
                          <button
                            key={slot.id}
                            onClick={() => slot.available > 0 && handleSlotSelect(slot)}
                            disabled={slot.available <= 0}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                              selectedSlot?.id === slot.id
                                ? 'border-burgundy bg-burgundy/5'
                                : slot.available > 0
                                  ? 'border-charcoal/20 hover:border-burgundy/50'
                                  : 'border-charcoal/10 bg-charcoal/5 opacity-60 cursor-not-allowed'
                            }`}
                            data-testid={`slot-${slot.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Clock size={16} className="text-charcoal/60" />
                                <span className="font-medium text-charcoal">
                                  {slot.start_time} - {slot.end_time}
                                </span>
                              </div>
                              {selectedSlot?.id === slot.id && (
                                <CheckCircle size={20} className="text-burgundy" />
                              )}
                            </div>
                            <div className="mt-2 flex items-center gap-2 text-sm">
                              <Users size={14} className="text-charcoal/40" />
                              <span className={slot.available > 5 ? 'text-green-600' : slot.available > 0 ? 'text-yellow-600' : 'text-red-600'}>
                                {slot.available > 0 ? `${slot.available} spots available` : 'Full'}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 border border-charcoal/20 text-charcoal rounded-xl hover:bg-charcoal/5 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    if (!selectedSlot) {
                      toast.error('Please select an appointment time');
                      return;
                    }
                    setStep(3);
                  }}
                  disabled={!selectedSlot}
                  className="flex-1 py-3 bg-burgundy text-white rounded-xl hover:bg-burgundy/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="next-step-2"
                >
                  Continue
                  <ChevronRight size={18} />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Review & Confirm */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <h2 className="text-xl font-serif text-charcoal mb-6 flex items-center gap-2">
                <QrCode className="text-burgundy" />
                Review & Confirm
              </h2>

              <div className="bg-cream rounded-xl p-6 mb-6">
                <h3 className="font-medium text-charcoal mb-4">Your Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-charcoal/60">Name</p>
                    <p className="font-medium text-charcoal">{formData.full_name}</p>
                  </div>
                  <div>
                    <p className="text-charcoal/60">Phone</p>
                    <p className="font-medium text-charcoal">{formData.phone}</p>
                  </div>
                  <div>
                    <p className="text-charcoal/60">Email</p>
                    <p className="font-medium text-charcoal">{formData.email || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-charcoal/60">Household Size</p>
                    <p className="font-medium text-charcoal">{formData.household_size} people</p>
                  </div>
                </div>
              </div>

              <div className="bg-burgundy/5 border border-burgundy/20 rounded-xl p-6 mb-6">
                <h3 className="font-medium text-burgundy mb-4">Your Appointment</h3>
                {selectedSlot && (
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-burgundy/10 rounded-xl flex items-center justify-center">
                      <Calendar className="text-burgundy" size={24} />
                    </div>
                    <div>
                      <p className="font-medium text-charcoal">
                        {new Date(selectedSlot.date + 'T00:00:00').toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                      <p className="text-charcoal/60">
                        {selectedSlot.start_time} - {selectedSlot.end_time}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mb-8">
                <p className="text-sm text-charcoal/70">
                  <strong>Important:</strong> Please arrive on time with a valid photo ID. 
                  If you need to cancel or reschedule, please call us at (407) 216-2853.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 border border-charcoal/20 text-charcoal rounded-xl hover:bg-charcoal/5 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 py-3 bg-burgundy text-white rounded-xl hover:bg-burgundy/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-registration"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Confirm Registration
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
